/**
 * `PairingPort` in HTTP.
 *
 * Tre chiamate contro il servizio centrale di accoppiamento, e nient'altro: la logica di cosa
 * fare dopo ogni esito sta nel dominio (`pairing.ts`), perché è quella che si sbaglia senza che
 * si veda e va provata senza un televisore.
 *
 * **Gli stati si leggono tutti, nessuno diventa un'eccezione generica.** `410` è «il codice è
 * morto, chiedine uno nuovo» e `204` è «aspetta ancora»: confonderli lascia il televisore a
 * interrogare per sempre un codice che non esiste. Solleva soltanto quando non c'è risposta
 * affatto — rete, timeout — perché quello è l'unico caso in cui il codice è ancora buono.
 */

import { PlayerError, parseGatewayError } from '@player/domain';
import type { CancelSignal, PairingPort, PairingPollResult, PairingRequest } from '@player/ports';

/**
 * Tempi massimi. Brevi: il codice vive novanta secondi, e un'attesa di trenta su
 * un'interrogazione che ne vale cinque si mangia un terzo della finestra.
 */
export const PAIRING_TIMEOUTS_MS = {
  create: 10_000,
  poll: 8_000,
} as const;

export interface PairingHttpOptions {
  /** Radice del servizio, senza `/v1` finale. */
  readonly baseUrl: string;
  /**
   * La chiave compilata nell'app. **Non è un segreto** — si estrae dal pacchetto in pochi minuti —
   * e sta scritto qui perché nessuno la scambi per quel che non è: serve a tenere fuori il
   * traffico casuale e a dare un appiglio al limite di frequenza, e nessuna decisione deve
   * appoggiarsi su di essa.
   */
  readonly playerKey: string;
  readonly fetchImpl: typeof fetch;
}

export class HttpPairingClient implements PairingPort {
  private readonly baseUrl: string;

  constructor(private readonly options: PairingHttpOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
  }

  async create(signal?: CancelSignal): Promise<PairingRequest> {
    const response = await this.send('POST', '/v1/requests', PAIRING_TIMEOUTS_MS.create, signal);

    if (response.status !== 200) throw this.fail('pairing.create_failed', response.status);

    const body = (await response.json()) as { code?: unknown; expiresIn?: unknown };
    const code = typeof body.code === 'string' ? body.code : '';
    const expiresIn = typeof body.expiresIn === 'number' ? body.expiresIn : 0;
    // Un corpo senza codice o senza scadenza non diventa una schermata con un riquadro vuoto:
    // mostrare un codice che non c'è è peggio che dire che il servizio ha risposto male.
    if (!/^[a-z0-9]{8}$/.test(code) || expiresIn <= 0) {
      throw new PlayerError(parseGatewayError(null, Date.now()));
    }

    return { code, expiresInSeconds: expiresIn };
  }

  async poll(code: string, signal?: CancelSignal): Promise<PairingPollResult> {
    const response = await this.send(
      'GET',
      `/v1/requests/${encodeURIComponent(code)}`,
      PAIRING_TIMEOUTS_MS.poll,
      signal,
    );

    if (response.status === 204) return { kind: 'waiting' };

    if (response.status === 410) return { kind: 'gone' };

    if (response.status === 429) {
      return { kind: 'throttled', retryAfterMs: retryAfterMs(response.headers.get('Retry-After')) };
    }

    if (response.status === 200) {
      const body = (await response.json()) as {
        token?: unknown;
        gateway?: unknown;
        serviceName?: unknown;
      };
      const token = typeof body.token === 'string' ? body.token : '';
      const host = typeof body.gateway === 'string' ? body.gateway : '';
      /*
        Una consegna a metà è irrecuperabile: il servizio ha **già cancellato** la richiesta
        consegnandola, quindi non c'è un secondo tentativo. Si solleva invece di entrare con
        credenziali monche, che fallirebbero dopo, sul gateway, dove nessuno collegherà le due cose.
      */
      if (token.length === 0 || host.length === 0) {
        throw new PlayerError(parseGatewayError(null, Date.now()));
      }
      /*
        Il nome non si pretende e non si valida: è testo da mostrare, e un'istanza più vecchia non
        lo manda affatto. Assente o di un tipo inatteso vale `null`, che chi disegna legge come
        «mostra l'host».
      */
      const serviceName = typeof body.serviceName === 'string' && body.serviceName !== ''
        ? body.serviceName
        : null;

      return { kind: 'ready', host, token, serviceName };
    }

    throw this.fail('pairing.poll_failed', response.status);
  }

  private async send(
    method: 'GET' | 'POST',
    path: string,
    timeoutMs: number,
    signal?: CancelSignal,
  ): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const onAbort = (): void => controller.abort();
    signal?.addEventListener('abort', onAbort);

    try {
      return await this.options.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers: { 'X-Player-Key': this.options.playerKey },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    }
  }

  private fail(code: string, status: number): PlayerError {
    return new PlayerError({
      code: 'app.malformed_response',
      params: { detail: code, status },
      retryable: true,
      atMs: Date.now(),
    });
  }
}

/**
 * `Retry-After` in millisecondi, se c'è ed è un numero di secondi.
 *
 * La forma con la data HTTP non si legge: dipenderebbe dall'orologio dell'apparecchio, che è la
 * cosa di cui qui non ci si fida. Senza un valore utilizzabile decide il dominio, che ha un'attesa
 * pensata per una finestra di novanta secondi.
 */
function retryAfterMs(header: string | null): number | null {
  if (header === null) return null;
  const seconds = Number(header.trim());
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return seconds * 1000;
}
